"use client";
import { useForm } from "react-hook-form";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { motion } from "framer-motion";
import { contactInfo } from "@/lib/utils";
import { useMemo, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { database } from "../../lib/firebase";

interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  service?: string;
  message: string;
}

const Contact = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>();

  const [submitStatus, setSubmitStatus] = useState<string | null>(null);

  const onSubmit = async (formData: FormData) => {
    try {
      const docData = {
        ...formData,
        timestamp: new Date().toISOString(),
        submittedAt: new Date(),
      };
      await addDoc(collection(database, "parth-connection"), docData);
      setSubmitStatus("Message sent successfully ✅");
      reset();
    } catch (error) {
      setSubmitStatus("Failed to send message ❌");
      console.error("Firebase error:", error);
    }
  };

  const renderedContactInfo = useMemo(() => {
    return contactInfo.map((item, index) => (
      <li key={index} className="flex items-center gap-6 cursor-target">
        <div className="w-[52px] h-[52px] xl:w-[72px] xl:h-[72px] bg-[#27272c] text-accent rounded-md flex items-center justify-center cursor-target">
          <div className="text-[28px] cursor-target">{item.icon}</div>
        </div>
        <div className="flex-1">
          <p className="text-white/60 cursor-target">{item.title}</p>
          <h3 className="text-xl cursor-target">{item.description}</h3>
        </div>
      </li>
    ));
  }, []);

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
      }}
      className="py-6"
    >
      <div className="container mx-auto">
        <h1 className="sr-only">Contact Parth Kalani</h1>
        <div className="flex flex-col xl:flex-row gap-[30px]">
          <div className="xl:w-[54%] order-2 xl:order-none">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6 p-10 bg-[#27272c] rounded-xl cursor-target"
            >
              <h3 className="text-4xl text-accent cursor-target">Let's work together</h3>
              <p className="text-white/60 cursor-target">
                Connect with me for exciting opportunities, collaborations, or any inquiries you may have.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  type="text"
                  placeholder="Firstname"
                  className="cursor-target"
                  {...register("firstname", { required: "First name is required" })}
                />
                <Input
                  type="text"
                  placeholder="Lastname"
                  className="cursor-target"
                  {...register("lastname", { required: "Last name is required" })}
                />
                <Input
                  type="email"
                  placeholder="Email address"
                  className="cursor-target"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  className="cursor-target"
                  {...register("phone")}
                />
              </div>
              <Select onValueChange={(value) => setValue("service", value)}>
                <SelectTrigger className="w-full cursor-target">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="cursor-target">Select a service</SelectLabel>
                    <SelectItem value="web" className="cursor-target">Web Development</SelectItem>
                    <SelectItem value="ui" className="cursor-target">UI/UX Design</SelectItem>
                    <SelectItem value="logo" className="cursor-target">Logo Design</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Textarea
                className="h-[200px] cursor-target"
                placeholder="Type your message here."
                {...register("message", { required: "Message is required" })}
              />
              {submitStatus && (
                <p className={`text-center cursor-target ${submitStatus.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>
                  {submitStatus}
                </p>
              )}
              <Button
                type="submit"
                size="md"
                className="max-w-40 cursor-target"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send message"}
              </Button>
            </form>
          </div>
          <div className="flex-1 flex items-center xl:justify-end order-1 xl:order-none mb-8 xl:mb-0">
            <ul className="flex flex-col gap-10">
              {renderedContactInfo}
            </ul>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Contact;
